import { Controller } from '@hotwired/stimulus';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

// Generates the Monthly Report in the background and downloads it when it is ready, instead of
// waiting inside the request: a large estate takes minutes to render and nginx closes the
// connection with a 504 long before the file exists.
export default class extends Controller {
  static targets = ['submit'];

  static values = {
    generatingText: String,
    generatingButton: String,
    readyButton: String,
    errorText: String,
    errorTimeoutText: String,
    errorWithReasonText: String,
  };

  connect() {
    this.originalButtonText = this.submitTarget.value;
    this.state = 'idle';
  }

  disconnect() {
    this.stopPolling();
  }

  async request(event) {
    event.preventDefault();

    if (this.state === 'generating') return;

    this.showGenerating();

    try {
      const response = await fetch(this.element.action, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.element),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.status === 'ready') {
        this.download(data.download_url);
      } else {
        this.statusUrl = data.status_url;
        this.notify(this.generatingTextValue, 'info');
        this.startPolling();
      }
    } catch {
      this.showError(null);
    }
  }

  startPolling() {
    // Idempotent: without clearing first, a second submit would overwrite the interval id and
    // leak the earlier timer, which would keep firing with nothing able to cancel it.
    this.stopPolling();
    this.pollingStartedAt = Date.now();
    this.pollTimer = setInterval(() => this.poll(), POLL_INTERVAL_MS);
  }

  async poll() {
    if (Date.now() - this.pollingStartedAt > POLL_TIMEOUT_MS) {
      this.showError('timeout');
      return;
    }

    try {
      const response = await fetch(this.statusUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.status === 'ready') {
        this.download(data.download_url);
      } else if (data.status === 'failed' || data.status === 'none') {
        this.showError(data.reason);
      }
    } catch {
      this.showError(null);
    }
  }

  // `window.location.assign` on an attachment response starts the download without navigating
  // the page away, so the form stays exactly as the user left it.
  download(url) {
    this.stopPolling();
    this.state = 'idle';
    this.submitTarget.value = this.readyButtonValue;
    this.submitTarget.classList.remove('disable');
    this.submitTarget.disabled = false;
    window.location.assign(url);
  }

  showGenerating() {
    this.state = 'generating';
    this.submitTarget.value = this.generatingButtonValue;
    this.submitTarget.classList.add('disable');
    this.submitTarget.disabled = true;
  }

  showError(reason) {
    this.stopPolling();
    this.restore();
    this.notify(this.errorMessageFor(reason), 'danger');
  }

  errorMessageFor(reason) {
    if (reason === 'timeout') return this.errorTimeoutTextValue;
    if (reason) return this.errorWithReasonTextValue.replace('%{reason}', reason);

    return this.errorTextValue;
  }

  restore() {
    this.state = 'idle';
    this.submitTarget.value = this.originalButtonText;
    this.submitTarget.classList.remove('disable');
    this.submitTarget.disabled = false;
  }

  // Same markup and `flash-message` controller as the server-rendered Rails flashes
  // (layouts/v2/_flash_messages.html.erb), inserted client-side so it looks identical and
  // auto-dismisses the same way, without blocking the page like `alert()` would.
  notify(message, type) {
    const container = document.getElementById('js-flash-messages');
    if (!container) return;

    const banner = document.createElement('div');
    banner.dataset.controller = 'flash-message';
    banner.dataset.flashMessageAutoDismissableValue = 'true';
    banner.className = `alert alert-dismissible alert-${type}`;
    banner.innerHTML =
      '<div data-flash-message-target="content"></div>' +
      '<div class="close" data-dismiss="alert"><span>&times;</span></div>';
    banner.querySelector('[data-flash-message-target="content"]').textContent = message;

    container.appendChild(banner);
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}
